import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BASE_CATEGORIES = ["Sexual solicitation", "Legal & protective", "Harm & violence", "Dignity & respect", "Learned from moderation decisions"];

export const FlagEditor = ({ flag, categories, onClose, onSaved }) => {
  const isNew = !flag.id;
  const [form, setForm] = useState({
    name: flag.name || "",
    category: flag.category || BASE_CATEGORIES[0],
    description: flag.description || "",
    active: flag.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const allCategories = Array.from(new Set([...BASE_CATEGORIES, ...(categories || [])]));

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Give the flag a name");
    if (form.category.trim().length < 2) return toast.error("Pick or type a category");
    if (form.description.trim().length < 10) return toast.error("Describe what the flag should catch (10+ characters)");
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/moderation/flags", form);
      else await api.put(`/admin/moderation/flags/${flag.id}`, form);
      toast.success(`"${form.name.trim()}" is ${form.active ? "live" : "saved (disabled)"}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the flag");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground";
  const inputCls = "mt-2 bg-background border-border rounded-sm focus-visible:ring-[#007AFF]";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-border rounded-sm max-w-2xl" data-testid="flag-editor">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{isNew ? "New moderation flag" : `Edit — ${flag.name}`}</DialogTitle>
          <DialogDescription className="text-xs">Active flags apply to the very next message any user tries to send.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Flag name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="flag-name-input"
                placeholder="e.g. Threatening language" className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>Category</Label>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} list="flag-categories"
                data-testid="flag-category-input" placeholder="Pick or type a new one" className={inputCls} />
              <datalist id="flag-categories">
                {allCategories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>
          <div>
            <Label className={labelCls}>Description — what should the AI catch?</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="flag-description-input"
              rows={5} placeholder="Describe the behaviour, including slang, coded phrases or examples the AI should recognise."
              className={`${inputCls} text-sm leading-relaxed resize-y`} />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={form.active} onCheckedChange={(v) => set("active", !!v)} data-testid="flag-active-checkbox" />
            Active (enforced on outgoing messages)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm" data-testid="flag-cancel-btn">Cancel</Button>
            <Button type="button" onClick={save} disabled={saving} data-testid="flag-save-btn"
              className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : isNew ? "Create flag" : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
